import { DatePicker } from '@/components/DatePicker'
import { FormDialog } from '@/components/FormDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { isApiError } from '@/utils/error'
import { getMutationOptions } from '@/utils/mutation'
import { showError, showSuccess } from '@/utils/toast'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreatePatient, useGetPatient, useUpdatePatient } from '../hooks/usePatient'
import { useSearchHisPatient } from '../hooks/useSearchHisPatient'
import { Patient } from '../types/patient'

const emptyForm = {
  search: '',

  hn: '',
  title: '',
  firstName: '',
  midName: '',
  lastName: '',
  sex: '',
  birthDate: '',
  bloodGroup: '',
  nationality: '',
  race: '',
  religion: '',
  job: '',
  cardId: '',
  passport: '',
  phoneNumber: '',
  address: '',
  email: '',
  emergencyName: '',
  emergencyPhoneNumber: '',
  emergencyRelation: '',
  historyMedicine: '',
  note: '',
}

function mapPatientToForm(patient: Patient) {
  return {
    search: '',

    hn: patient.hn ?? '',
    title: patient.title ?? '',
    firstName: patient.firstName ?? '',
    midName: patient.midName ?? '',
    lastName: patient.lastName ?? '',
    sex: patient.sex ?? '',
    birthDate: patient.birthDate ?? '',
    bloodGroup: patient.bloodGroup ?? '',
    nationality: patient.nationality ?? '',
    race: patient.race ?? '',
    religion: patient.religion ?? '',
    job: patient.job ?? '',
    cardId: patient.cardId ?? '',
    passport: patient.passport ?? '',
    phoneNumber: patient.phoneNumber ?? '',
    address: patient.address ?? '',
    email: patient.email ?? '',
    emergencyName: patient.emergencyName ?? '',
    emergencyPhoneNumber: patient.emergencyPhoneNumber ?? '',
    emergencyRelation: patient.emergencyRelation ?? '',
    historyMedicine: patient.historyMedicine ?? '',
    note: patient.note ?? '',
  }
}

export function PatientDialog({
  open,
  onOpenChange,
  hisPatient,
  patientId,
  onSuccess,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  hisPatient?: Patient | null
  patientId?: string | null
  onSuccess?: (patient: Patient) => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState(emptyForm)
  const { data: patient, isFetching: LoadingPatient } = useGetPatient(patientId!, !!patientId && open)
  const createPatient = useCreatePatient()
  const updatePatient = useUpdatePatient()
  const getHisPatient = useSearchHisPatient()

  const [hisNotFound, setHisNotFound] = useState(false)

  const isEdit = !!patient

  useEffect(() => {
    setHisNotFound(false)

    if (patient) {
      setForm(mapPatientToForm(patient))
    } else if (hisPatient) {
      setForm(mapPatientToForm(hisPatient))
    } else {
      setForm(emptyForm)
    }
  }, [patient, hisPatient, open])

  const isInvalid =
    !form.hn || !form.cardId || !form.title || !form.firstName || !form.lastName || !form.sex || !form.birthDate
  const isLoading = LoadingPatient || createPatient.isPending || updatePatient.isPending

  async function handleSubmit() {
    if (isInvalid) return

    const options = getMutationOptions({
      successMessage: isEdit ? t('patient.toast.editSuccess') : t('patient.toast.addSuccess'),
      onSuccess: () => {
        onOpenChange(false)
      },
    })

    if (isEdit && patient) {
      updatePatient.mutate({ id: patient.id, payload: form }, options)
    } else {
      try {
        const createdPatient = await createPatient.mutateAsync(form)

        showSuccess(t('patient.toast.addSuccess'))
        onSuccess?.(createdPatient)
        onOpenChange(false)
      } catch (error) {
        if (isApiError(error)) {
          showError(error.message)
        }
      }
    }
  }

  async function searchHis() {
    if (!form.search) return

    const his = await getHisPatient.mutateAsync(form.search)
    if (!his) {
      setHisNotFound(true)
      return
    }

    setHisNotFound(false)
    setForm({
      ...emptyForm,
      hn: his.hn ?? '',
      title: his.title ?? '',
      firstName: his.firstName ?? '',
      lastName: his.lastName ?? '',
      sex: his.gender ?? '',
      birthDate: his.birthDate ?? '',
      cardId: his.nationalId ?? '',
    })
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? t('patient.edit') : t('patient.add')}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      isInvalid={isInvalid}
      dialogContentClass="sm:max-w-4xl"
      contentTestId="patient-dialog"
      submitTestId="patient-form-submit"
    >
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-2">
        {!isEdit && (
          <>
            <div className="flex flex-col gap-1.5 sm:col-span-1">
              <Label className="text-xs">{t('patient.searchHis')}</Label>
              <Input
                value={form.search}
                data-testid="patient-form-search-his"
                onChange={e => {
                  setForm({ ...form, search: e.target.value })
                  setHisNotFound(false)
                }}
                className="h-9 bg-secondary/50 text-xs"
                placeholder=""
              />
              <span data-testid="patient-form-his-not-found" className={`text-xs text-muted-foreground ${hisNotFound ? 'visible' : 'invisible'}`}>
                {t('patient.searchHisNotFound')}
              </span>
            </div>
            <div className="flex flex-col gap-1.5 items-start justify-center col-span-1">
              <Button onClick={searchHis} data-testid="patient-form-search-his-button" className="text-xs" disabled={!form.search || getHisPatient.isPending}>
                {getHisPatient.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                {t('common.search')}
              </Button>
            </div>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pt-2">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.hn')} *</Label>
          <Input
            value={form.hn}
            data-testid="patient-form-hn"
            onChange={e => setForm({ ...form, hn: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.cardId')} *</Label>
          <Input
            value={form.cardId}
            data-testid="patient-form-card-id"
            onChange={e => setForm({ ...form, cardId: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.titleName')} *</Label>
          <Input
            value={form.title}
            data-testid="patient-form-title"
            onChange={e => setForm({ ...form, title: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.firstName')} *</Label>
          <Input
            value={form.firstName}
            data-testid="patient-form-first-name"
            onChange={e => setForm({ ...form, firstName: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.midName')}</Label>
          <Input
            value={form.midName}
            data-testid="patient-form-mid-name"
            onChange={e => setForm({ ...form, midName: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.lastName')} *</Label>
          <Input
            value={form.lastName}
            data-testid="patient-form-last-name"
            onChange={e => setForm({ ...form, lastName: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.birthdate')} *</Label>
          <DatePicker value={form.birthDate} testId="patient-form-birth-date" onChange={birthDate => setForm({ ...form, birthDate })} placeholder="" />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.sex')} *</Label>
          <Select value={form.sex || 'none'} onValueChange={v => setForm({ ...form, sex: v })}>
            <SelectTrigger className="h-9 bg-secondary/50 text-xs w-full" data-testid="patient-form-sex">
              <SelectValue placeholder="" />
            </SelectTrigger>
            <SelectContent className="bg-card text-foreground">
              <SelectItem value="none">- {t('patient.unknown')} -</SelectItem>
              <SelectItem value="Male" data-testid="patient-form-sex-option-Male">{t('patient.male')}</SelectItem>
              <SelectItem value="Female" data-testid="patient-form-sex-option-Female">{t('patient.female')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.bloodGroup')}</Label>
          <Input
            value={form.bloodGroup}
            data-testid="patient-form-blood-group"
            onChange={e => setForm({ ...form, bloodGroup: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.nationality')}</Label>
          <Input
            value={form.nationality}
            data-testid="patient-form-nationality"
            onChange={e => setForm({ ...form, nationality: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.race')}</Label>
          <Input
            value={form.race}
            data-testid="patient-form-race"
            onChange={e => setForm({ ...form, race: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.religion')}</Label>
          <Input
            value={form.religion}
            data-testid="patient-form-religion"
            onChange={e => setForm({ ...form, religion: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.job')}</Label>
          <Input
            value={form.job}
            data-testid="patient-form-job"
            onChange={e => setForm({ ...form, job: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.passport')}</Label>
          <Input
            value={form.passport}
            data-testid="patient-form-passport"
            onChange={e => setForm({ ...form, passport: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.phoneNumber')}</Label>
          <Input
            value={form.phoneNumber}
            data-testid="patient-form-phone-number"
            onChange={e => setForm({ ...form, phoneNumber: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.address')}</Label>
          <Textarea
            value={form.address}
            data-testid="patient-form-address"
            onChange={e => setForm({ ...form, address: e.target.value })}
            className="bg-secondary/50 text-xs"
            rows={2}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.email')}</Label>
          <Input
            value={form.email}
            data-testid="patient-form-email"
            onChange={e => setForm({ ...form, email: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.emergencyName')}</Label>
          <Input
            value={form.emergencyName}
            data-testid="patient-form-emergency-name"
            onChange={e => setForm({ ...form, emergencyName: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.emergencyPhoneNumber')}</Label>
          <Input
            value={form.emergencyPhoneNumber}
            data-testid="patient-form-emergency-phone"
            onChange={e => setForm({ ...form, emergencyPhoneNumber: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('patient.emergencyRelation')}</Label>
          <Input
            value={form.emergencyRelation}
            data-testid="patient-form-emergency-relation"
            onChange={e => setForm({ ...form, emergencyRelation: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5 sm:col-span-2 md:col-span-3">
          <Label className="text-xs">{t('patient.historyMedicine')}</Label>
          <Input
            value={form.historyMedicine}
            data-testid="patient-form-history-medicine"
            onChange={e => setForm({ ...form, historyMedicine: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5 sm:col-span-2 md:col-span-3">
          <Label className="text-xs">{t('patient.note')}</Label>
          <Input
            value={form.note}
            data-testid="patient-form-note"
            onChange={e => setForm({ ...form, note: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
      </div>
    </FormDialog>
  )
}
